import React, { useEffect, useState, useRef, memo, useMemo } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiEye, FiEdit3, FiTrash2 } from "react-icons/fi";
import { Link } from "react-router-dom";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

export const formatPhone = (phone) => {
  if (!phone) return "-";

  // Sadece rakamları al
  let digits = phone.toString().replace(/\D/g, "");

  // +90 ile gelenler (90555...)
  if (digits.startsWith("90") && digits.length === 12) {
    return `+90 (${digits.slice(2, 5)}) ${digits.slice(5, 8)} ${digits.slice(8, 10)} ${digits.slice(10, 12)}`;
  }

  // 0 ile başlayanlar (0555...)
  if (digits.startsWith("0") && digits.length === 11) {
    return `0 (${digits.slice(1, 4)}) ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9, 11)}`;
  }

  // Direkt 5 ile başlayanlar (555...)
  if (digits.startsWith("5") && digits.length === 10) {
    return `0 (${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  }

  // Diğer ülke kodları (+49123...)
  if (digits.length > 11) {
    const countryCode = digits.slice(0, digits.length - 10);
    const number = digits.slice(-10);
    return `+${countryCode} (${number.slice(0, 3)}) ${number.slice(3, 6)} ${number.slice(6, 8)} ${number.slice(8, 10)}`;
  }

  return phone; // fallback
};

// 1) Durum Switch Bileşeni
const TableCell = memo(({ adminId, fetchAdmin }) => {
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    const fetchAdminStatus = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/leads/status/${adminId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
            },
          },
        );
        setIsChecked(response.data.status === "active");
      } catch (error) {
        console.error("❌ Kullanıcı durumu alınamadı:", error);
      }
    };
    fetchAdminStatus();
  }, [adminId]);

  const toggleStatus = async () => {
    const newStatus = isChecked ? "inactive" : "active";
    setIsChecked(!isChecked);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/leads/update-status/${adminId}`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
          },
        },
      );
    } catch (error) {
      console.error("❌ Durum güncellenemedi:", error);
      setIsChecked(isChecked);
    }
  };

  return (
    <div className="form-check form-switch text-end d-flex align-items-end justify-content-end">
      <input
        className="form-check-input custom-checkbox"
        type="checkbox"
        role="switch"
        checked={isChecked}
        onChange={toggleStatus}
        style={{
          cursor: "pointer",
          width: "45px",
          height: "22px",
          transition: "0.3s ease-in-out",
        }}
      />
      <span
        className={`badge ms-2 ${isChecked ? "badge-success" : "badge-danger"}`}
        style={{
          padding: "6px 12px",
          borderRadius: "8px",
          fontSize: "14px",
          fontWeight: "bold",
          transition: "0.3s ease-in-out",
        }}
      ></span>
    </div>
  );
});

// 2) Ana Bileşen
const LeadsTable = () => {
  const [admins, setAdmins] = useState([]);
  const [myPermissions, setMyPermissions] = useState([]);

  const language = useUserLanguage();
  const { translations } = useTranslations(language);

  const getText = useMemo(() => {
    // translations gelmediğindeyse defaultValue dön
    return (key, defaultValue) => translations[key] || defaultValue;
  }, [translations]);

  const handleSoftDelete = (id) => {
    confirmAlert({
      title: getText("SOFT_DELETE_CONFIRM_TITLE"),
      message: getText("SOFT_DELETE_CONFIRM_MSG"),
      buttons: [
        {
          label: getText("YES_SOFT_DELETE"),
          onClick: async () => {
            try {
              const token = localStorage.getItem("admin_token");
              await axios.delete(
                `${process.env.REACT_APP_API_BASE_URL}/api/admin/leads/${id}`,
                { headers: { Authorization: `Bearer ${token}` } },
              );
              toast.success(getText("SUCCESS_USER_SOFT_DELETED"));
              fetchAdmin(); //  Listeleri yenile
            } catch (err) {
              console.error("Silme işlemi başarısız:", err);
              toast.error(getText("ERROR_USER_SOFT_DELETE_FAILED"));
            }
          },
        },
        {
          label: getText("CANCEL"),
          onClick: () => toast.info(getText("SOFT_DELETE_CANCELLED")),
        },
      ],
    });
  };

  const fetchAdmin = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const myRole = localStorage.getItem("userRole"); // 👈 BENİM ROLÜM

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/leads/list`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      let data = response.data;

      // 🔒 Eğer ben superadmin değilsem → superadminleri gizle
      if (myRole !== "superadmin") {
        data = data.filter((item) => item.role_name !== "superadmin");
      }

      setAdmins(data);
    } catch (error) {
      console.error("❌ Kullanıcı verileri alınamadı:", error);
    }
  };

  const fetchMyPermissions = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/my-permissions`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!resp.data || !Array.isArray(resp.data.permissions)) {
        console.error("⚠️ API yanlış veri döndürdü:", resp.data);
        setMyPermissions([]);
      } else {
        setMyPermissions(resp.data.permissions);
      }
    } catch (error) {
      console.error("❌ Permission bilgileri çekilemedi:", error);
      setMyPermissions([]);
    }
  };

  useEffect(() => {
    fetchAdmin();
    fetchMyPermissions();
  }, []);

  useEffect(() => {
    console.log("📌 State içindeki Admin Verileri:", admins);
  }, [admins]);

  // "Yönetici Hesapları" modülünün izin kaydı
  const adminModulePerm = myPermissions.find(
    (p) => p.module_key === "static_admin_accounts",
  );
  const canView =
    adminModulePerm?.view_permission || adminModulePerm?.full_access;
  const canEdit =
    adminModulePerm?.edit_permission || adminModulePerm?.full_access;
  const canDelete =
    adminModulePerm?.delete_permission || adminModulePerm?.full_access;

  // Tablo Kolonları (useMemo)
  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: () => getText("TABLE_ID_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 80,
      },
      {
        accessorKey: "adminImage",
        header: () => getText("TABLE_PROFILE_IMAGE_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 100,
        enableSorting: false,
        cell: (info) => {
          const imagePath = info.row.original.customer?.img;
          const imageUrl = imagePath
            ? `${process.env.REACT_APP_API_BASE_URL}${imagePath}`
            : `${process.env.REACT_APP_API_BASE_URL}/uploads/avatars/default.svg`;
          return (
            <img
              src={imageUrl}
              alt="Profil"
              onError={(e) =>
                (e.target.src = `${process.env.REACT_APP_API_BASE_URL}/uploads/avatars/default.svg`)
              }
              style={{
                width: "45px",
                height: "45px",
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid #ddd",
              }}
            />
          );
        },
      },
      {
        accessorKey: "customer",
        header: () => getText("TABLE_NAME_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 200,
        cell: (info) => {
          const admin = info.row.original.customer;
          return <span>{admin?.name}</span>;
        },
      },
      {
        accessorKey: "email",
        header: () => getText("TABLE_EMAIL_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 250,
        cell: (info) => (
          <a
            href={`mailto:${info.getValue()}`}
            style={{ fontWeight: "normal" }}
          >
            {info.getValue()}
          </a>
        ),
      },
      {
        accessorKey: "phone",
        header: () => getText("TABLE_PHONE_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 170,
        cell: (info) => {
          const rawPhone = info.getValue();
          const formattedPhone = formatPhone(rawPhone);

          return (
            <a
              href={`tel:${rawPhone}`}
              style={{
                fontWeight: "500",
                textDecoration: "none",
                color: "inherit",
                letterSpacing: "0.3px",
              }}
            >
              {formattedPhone}
            </a>
          );
        },
      },

      {
        accessorKey: "date",
        header: () => (
          <div style={{ textAlign: "end" }}>
            {getText("TABLE_DATE_LABEL")}
          </div>
        ),
        meta: {
          headerClassName: "text-end",
          className: "text-end date-column",
        },
        size: 120,
        cell: (info) => {
          const rawDate = info.getValue();
          const formattedDate = new Date(rawDate).toLocaleDateString("en-GB");
          return <div style={{ textAlign: "end" }}>{formattedDate}</div>;
        },
      },
      {
        accessorKey: "status",
        header: () => (
          <div style={{ textAlign: "end" }}>
            {getText("TABLE_STATUS_LABEL")}
          </div>
        ),
        meta: {
          headerClassName: "text-end",
          className: "text-end status-column",
        },
        size: 130,
        cell: (info) => {
          const adminId = info.row.original.id;
          return (
            <div style={{ textAlign: "end" }}>
              <TableCell adminId={adminId} fetchAdmin={fetchAdmin} />
            </div>
          );
        },
      },
      {
        accessorKey: "actions",
        header: () => (
          <div style={{ textAlign: "right", paddingRight: "50px" }}>
            {getText("TABLE_ACTIONS_LABEL")}
          </div>
        ),
        enableSorting: false,
        meta: { headerClassName: "text-end", className: "text-end" },
        size: 180,
        cell: (info) => {
          const id = info.row.original.id;
          const adminId = info.row.original.id;
          return (
            <div
              className="hstack gap-2 justify-content-end"
              style={{ paddingRight: "20px" }}
            >
              {canView && (
                <Link
                  to={`/leads/view/${adminId}`}
                  className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
                  title={getText("VIEW")}
                >
                  <FiEye />
                </Link>
              )}
              {canEdit && (
                <Link
                  to={`/profile/edit/${adminId}`}
                  className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
                  title={getText("EDIT")}
                >
                  <FiEdit3 />
                </Link>
              )}
              {canDelete && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault(); // sayfa yenilemeyi engelle
                    handleSoftDelete(id);
                  }}
                  className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
                  title={getText("DELETE")}
                >
                  <FiTrash2 />
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [
      getText,
      canView,
      canEdit,
      canDelete,
      admins,
      handleSoftDelete,
      fetchAdmin,
    ],
  );

  if (admins.length === 0) {
    return <p>Yükleniyor...</p>;
  }

  return <Table data={admins} columns={columns} />;
};

export default LeadsTable;
